import { MouseEventHandler, forwardRef, ForwardedRef } from 'react';

type ImgProps = {
	src?: string | null;
	width?: number;
	height?: number;
	imgWidth?: number;
	imgHeight?: number;
	alt: string;
	className?: string;
	transparent?: boolean;
	onClick?: MouseEventHandler<HTMLImageElement>;
};
export const Img = forwardRef(
	(
		{ src, width, height, alt, transparent = false, imgWidth = 0, imgHeight = 0, ...props }: ImgProps,
		ref: ForwardedRef<HTMLImageElement>
	) => {
		return (
			<picture>
				<source
					srcSet={
						imageSizeFormat(src, { width, height, format: 'webp', transparent: transparent }) +
						' 1x' +
						', ' +
						imageSizeFormat(src, { width, height, format: 'webp', scale: '2x', transparent: transparent }) +
						' 2x'
					}
					type="image/webp"
				/>
				<source
					srcSet={
						imageSizeFormat(src, { width, height }) +
						' 1x' +
						', ' +
						imageSizeFormat(src, { width, height, scale: '2x' }) +
						' 2x'
					}
					type={getImageMime(src)}
				/>
				<img
					ref={ref}
					src={imageSizeFormat(src, { width, height })}
					alt={alt}
					{...props}
					{...(imgWidth > 0 && { width: imgWidth })}
					{...(imgHeight > 0 && { height: imgHeight })}
					loading="lazy"
				/>
			</picture>
		);
	}
);

const mimeByExtension: Record<string, string> = {
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	png: 'image/png',
	gif: 'image/gif',
	webp: 'image/webp',
};
const extensionRegex = /\.([^.?#\s]+)(?:[?#].*)?$/;
const imageUrlRegex = /^(.*?)(?:_\d+_\d+(?:_2x)?(?:_tr)?)?(\.(?:jpe?g|png|gif|webp))([?#].*)?$/i;

type Size = {
	width?: number;
	height?: number;
	format?: string;
	scale?: string;
	transparent?: boolean;
};
export function imageSizeFormat(
	url: string | null | undefined,
	{ width, height, format, scale = '', transparent = false }: Size
): string {
	const imageUrl = url?.trim();
	if (!imageUrl) {
		return '/images/no-image.svg';
	}

	const match = imageUrlRegex.exec(imageUrl);
	if (match) {
		const [, baseUrl, extension, query = ''] = match;
		return (
			baseUrl +
			`_${width}_${height}` +
			(scale != '' ? '_' + scale : '') +
			(transparent ? '_tr' : '') +
			(format ? '.' + format : extension) +
			query
		);
	}
	return '/images/no-image.svg';
}
export function getImageMime(url: string | null | undefined): string {
	const ext = extensionRegex.exec(url?.trim() ?? '');
	if (ext && ext[1]) {
		return mimeByExtension[ext[1].toLowerCase()] ?? '';
	}
	return '';
}
